import type { Task } from './models';
import { taskIsPastEta } from './etaClock';

export type TaskFilterCombineMode = 'or' | 'and';

export interface TaskFilterSelection {
  tags: string[];
  primaryOwnerOnly: boolean;
  etaExceededOnly: boolean;
  todayOnly: boolean;
  blockedOnly: boolean;
  closedDoneOnly: boolean;
  closedNotDoneOnly: boolean;
  closureRequestOnly: boolean;
  notAssignedOnly: boolean;
  openOnly: boolean;
  unreadOnly: boolean;
  createdByMeOnly: boolean;
  /** Tasks where the current user was @mentioned in chat (not assignee/creator). */
  mentionedMeOnly: boolean;
  /** How multiple active filters combine. Default: OR. */
  combineMode: TaskFilterCombineMode;
}

export function emptyTaskFilterSelection(): TaskFilterSelection {
  return {
    tags: [],
    primaryOwnerOnly: false,
    etaExceededOnly: false,
    todayOnly: false,
    blockedOnly: false,
    closedDoneOnly: false,
    closedNotDoneOnly: false,
    closureRequestOnly: false,
    notAssignedOnly: false,
    openOnly: false,
    unreadOnly: false,
    createdByMeOnly: false,
    mentionedMeOnly: false,
    combineMode: 'or',
  };
}

export function isFilterEmpty(s: TaskFilterSelection): boolean {
  return (
    s.tags.length === 0 &&
    !s.primaryOwnerOnly &&
    !s.etaExceededOnly &&
    !s.todayOnly &&
    !s.blockedOnly &&
    !s.closedDoneOnly &&
    !s.closedNotDoneOnly &&
    !s.closureRequestOnly &&
    !s.notAssignedOnly &&
    !s.openOnly &&
    !s.unreadOnly &&
    !s.createdByMeOnly &&
    !s.mentionedMeOnly
  );
}

export function normalizeCombineMode(
  value: unknown,
): TaskFilterCombineMode {
  return value === 'and' ? 'and' : 'or';
}

export type TaskFilterToggleField = Exclude<
  keyof TaskFilterSelection,
  'tags' | 'combineMode'
>;

const TOGGLE_FILTER_ENTRIES: { field: TaskFilterToggleField; label: string }[] = [
  { field: 'blockedOnly', label: 'Blocked' },
  { field: 'closedDoneOnly', label: 'Closed · done' },
  { field: 'closedNotDoneOnly', label: 'Closed · not done' },
  { field: 'closureRequestOnly', label: 'Closure requested' },
  { field: 'createdByMeOnly', label: 'Created by me' },
  { field: 'todayOnly', label: 'Due today' },
  { field: 'etaExceededOnly', label: 'ETA exceeded' },
  { field: 'mentionedMeOnly', label: 'Mentioned me' },
  { field: 'notAssignedOnly', label: 'Unassigned' },
  { field: 'openOnly', label: 'Open' },
  { field: 'primaryOwnerOnly', label: 'Primary assignee is me' },
  { field: 'unreadOnly', label: 'Unread' },
];

/** Toggle filters shown in Advanced filters / Quick filters pickers. */
export const TASK_FILTER_TOGGLE_OPTIONS: ReadonlyArray<{
  field: TaskFilterToggleField;
  label: string;
}> = TOGGLE_FILTER_ENTRIES;

export const QUICK_FILTER_MAX = 10;

export function quickFilterTagId(tag: string): string {
  return `tag:${tag.trim().toLowerCase()}`;
}

export function isQuickFilterToggleId(id: string): id is TaskFilterToggleField {
  return TOGGLE_FILTER_ENTRIES.some((e) => e.field === id);
}

export function normalizeQuickFilterIds(raw: unknown): string[] {
  if (!Array.isArray(raw)) return [];
  const out: string[] = [];
  const seen = new Set<string>();
  for (const item of raw) {
    if (typeof item !== 'string') continue;
    const id = item.trim();
    if (!id || seen.has(id)) continue;
    if (id.startsWith('tag:')) {
      const tag = id.slice(4).trim().toLowerCase();
      if (!tag) continue;
      const normalized = `tag:${tag}`;
      if (seen.has(normalized)) continue;
      seen.add(normalized);
      out.push(normalized);
    } else if (isQuickFilterToggleId(id)) {
      seen.add(id);
      out.push(id);
    }
    if (out.length >= QUICK_FILTER_MAX) break;
  }
  return out;
}

export function quickFilterLabel(
  id: string,
  tagDisplayNames?: Map<string, string>,
): string {
  if (id.startsWith('tag:')) {
    const lower = id.slice(4);
    return tagDisplayNames?.get(lower) ?? lower;
  }
  const hit = TOGGLE_FILTER_ENTRIES.find((e) => e.field === id);
  return hit?.label ?? id;
}

/** Build a filter selection that applies only this quick filter. */
export function selectionFromQuickFilterId(id: string): TaskFilterSelection {
  const base = emptyTaskFilterSelection();
  if (id.startsWith('tag:')) {
    const tag = id.slice(4).trim();
    if (!tag) return base;
    return { ...base, tags: [tag] };
  }
  if (isQuickFilterToggleId(id)) {
    return { ...base, [id]: true };
  }
  return base;
}

/** Which configured quick filter (if any) is the sole active advanced filter. */
export function activeQuickFilterId(
  sel: TaskFilterSelection,
  configuredIds: string[],
): string | null {
  if (configuredIds.length === 0) return null;
  const chips = activeTaskFilterChips(sel);
  if (chips.length !== 1) return null;
  const id = chips[0]!.id;
  return configuredIds.includes(id) ? id : null;
}

export interface ActiveTaskFilterChip {
  id: string;
  label: string;
}

/** Active advanced filters in sidebar display order (toggles then tags). */
export function activeTaskFilterChips(sel: TaskFilterSelection): ActiveTaskFilterChip[] {
  const chips: ActiveTaskFilterChip[] = [];
  for (const { field, label } of TOGGLE_FILTER_ENTRIES) {
    if (sel[field]) chips.push({ id: field, label });
  }
  for (const tag of sel.tags) {
    const trimmed = tag.trim();
    if (!trimmed) continue;
    chips.push({ id: `tag:${trimmed.toLowerCase()}`, label: trimmed });
  }
  return chips;
}

export function removeTaskFilterChip(
  sel: TaskFilterSelection,
  chipId: string,
): TaskFilterSelection {
  if (chipId.startsWith('tag:')) {
    const lower = chipId.slice(4);
    return {
      ...sel,
      tags: sel.tags.filter((t) => t.trim().toLowerCase() !== lower),
    };
  }
  const field = chipId as TaskFilterToggleField;
  if (field in sel) {
    return { ...sel, [field]: false };
  }
  return sel;
}

function etaIsTodayWall(eta: Date, wallNow: Date): boolean {
  return (
    eta.getFullYear() === wallNow.getFullYear() &&
    eta.getMonth() === wallNow.getMonth() &&
    eta.getDate() === wallNow.getDate()
  );
}

type FilterCtx = {
  uid: string;
  unreadByTaskKey: Record<string, number>;
  unreadKey: (companyId: string, taskId: string) => string;
  /** Composite keys (`companyId__taskId`) where the user was @mentioned in chat. */
  mentionedTaskKeys: ReadonlySet<string>;
};

/** One boolean per active chip (toggle or individual tag). */
function activeCriterionMatches(
  t: Task,
  sel: TaskFilterSelection,
  ctx: FilterCtx,
  now: Date,
): boolean[] {
  const matches: boolean[] = [];
  const taskTags = new Set(
    t.tags.map((x) => x.trim().toLowerCase()).filter(Boolean),
  );

  for (const { field } of TOGGLE_FILTER_ENTRIES) {
    if (!sel[field]) continue;
    switch (field) {
      case 'primaryOwnerOnly':
        matches.push(t.primaryAssigneeId?.trim() === ctx.uid);
        break;
      case 'todayOnly':
        matches.push(t.eta != null && etaIsTodayWall(t.eta, now));
        break;
      case 'blockedOnly':
        matches.push(t.isBlocked);
        break;
      case 'closedDoneOnly':
        matches.push(t.status === 'closed' && !t.closedAsNotDone);
        break;
      case 'closedNotDoneOnly':
        matches.push(t.status === 'closed' && !!t.closedAsNotDone);
        break;
      case 'openOnly':
        matches.push(t.status === 'open');
        break;
      case 'closureRequestOnly':
        matches.push(!!t.hasClosureRequest);
        break;
      case 'notAssignedOnly':
        matches.push(t.assignees.length === 0 && t.supervisors.length === 0);
        break;
      case 'unreadOnly': {
        const n = ctx.unreadByTaskKey[ctx.unreadKey(t.companyId, t.id)] ?? 0;
        matches.push(n > 0);
        break;
      }
      case 'createdByMeOnly':
        matches.push(!!ctx.uid && t.createdBy.trim() === ctx.uid.trim());
        break;
      case 'mentionedMeOnly': {
        const uid = ctx.uid.trim();
        const key = ctx.unreadKey(t.companyId, t.id);
        // Prefer live message keys (collection-group). Also accept task denorm so
        // the filter still works while CG indexes build or the listener is empty.
        matches.push(
          ctx.mentionedTaskKeys.has(key) ||
            (!!uid && t.mentionedUserIds.includes(uid)),
        );
        break;
      }
      case 'etaExceededOnly':
        matches.push(t.status === 'open' && taskIsPastEta(t, now));
        break;
      default:
        break;
    }
  }

  for (const tag of sel.tags) {
    const lower = tag.trim().toLowerCase();
    if (!lower) continue;
    matches.push(taskTags.has(lower));
  }

  return matches;
}

/**
 * Applies advanced filters. Multiple active criteria combine with
 * [sel.combineMode] (`or` default, or `and`).
 */
export function applyTaskFilters(
  tasks: Task[],
  sel: TaskFilterSelection,
  ctx: FilterCtx,
): Task[] {
  if (isFilterEmpty(sel)) return tasks;

  const now = new Date();
  const mode = normalizeCombineMode(sel.combineMode);

  let list = tasks.filter((t) => {
    const matches = activeCriterionMatches(t, sel, ctx, now);
    if (matches.length === 0) return true;
    return mode === 'and' ? matches.every(Boolean) : matches.some(Boolean);
  });

  if (sel.etaExceededOnly) {
    list = [...list].sort((a, b) => {
      const ae = a.eta;
      const be = b.eta;
      if (ae == null && be == null) return 0;
      if (ae == null) return 1;
      if (be == null) return -1;
      return ae.getTime() - be.getTime();
    });
  }

  return list;
}

export type TaskFilterCountContext = FilterCtx;

/** Count tasks matching a single toggle (criterion alone). */
export function countTasksForFilterToggle(
  tasks: Task[],
  field: TaskFilterToggleField,
  ctx: TaskFilterCountContext,
): number {
  const sel = emptyTaskFilterSelection();
  sel[field] = true;
  return applyTaskFilters(tasks, sel, ctx).length;
}

/** Count tasks that carry [tag] (case-insensitive, same as applyTaskFilters). */
export function countTasksForFilterTag(
  tasks: Task[],
  tag: string,
  ctx: TaskFilterCountContext,
): number {
  const trimmed = tag.trim();
  if (!trimmed) return 0;
  const sel = emptyTaskFilterSelection();
  sel.tags = [trimmed];
  return applyTaskFilters(tasks, sel, ctx).length;
}

export function filterOptionLabelWithCount(label: string, count: number): string {
  return `${label} (${count})`;
}
